import { FlexRow } from '@/components/common/Flex';
import { FlexProps } from '@chakra-ui/react';
import { TOASTS } from '@common/toasts';
import { MAX_FILE_UPLOAD_SIZE_IN_BYTES } from '@common/uploads';
import { isNumber } from '@common/util/types';
import { ChangeEvent, useState } from 'react';
import ActionIconButton from '../common/ActionIconButton';
import { PaperClip, XCircle } from '../common/icons';
import { useErrorToast } from '../common/Toasts';
import { Text } from '../common/Typography';

interface AttachmentInputProps extends FlexProps {
    fileInputRef: React.RefObject<HTMLInputElement>;
}

const AttachmentsInput = ({ fileInputRef, ...props }: AttachmentInputProps) => {
    const [fileName, setFileName] = useState('');
    const showErrorToast = useErrorToast();

    const onChangeFileInput = (event: ChangeEvent<HTMLInputElement>) => {
        const largestFileSizeInBytes = getLargestFileSizeInBytes(event.target);
        if (
            isNumber(largestFileSizeInBytes) &&
            largestFileSizeInBytes > MAX_FILE_UPLOAD_SIZE_IN_BYTES
        ) {
            clearFileInput();
            showErrorToast(TOASTS.attachmentTooLarge);
            return;
        }
        setFileName(getFileName(event.target));
    };

    const openFileChooser = () => {
        fileInputRef.current?.click();
    };

    const clearFileInput = () => {
        if (fileInputRef.current) {
            // It looks odd, but this seems to be the best way to clear a file input
            fileInputRef.current.value = '';
            setFileName(getFileName(fileInputRef.current));
        }
    };

    return (
        <FlexRow minW={0} flexGrow={1} {...props} alignItems="center">
            <input
                type="file"
                onChange={onChangeFileInput}
                ref={fileInputRef}
                multiple
                hidden
            />
            <ActionIconButton
                label="Attach"
                size="xs"
                icon={<PaperClip />}
                onClick={openFileChooser}
                onClickEvent="Feedback Attach File Button Clicked"
            />
            {fileName && (
                <>
                    <Text
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        ml={2}
                    >
                        {fileName}
                    </Text>
                    <ActionIconButton
                        label="Remove"
                        size="xs"
                        colorScheme="gray"
                        icon={<XCircle size="md" />}
                        onClick={clearFileInput}
                        onClickEvent="Feedback Remove File Button Clicked"
                        ml={1}
                    />
                </>
            )}
        </FlexRow>
    );
};

export default AttachmentsInput;

export const getFileName = (input: HTMLInputElement): string =>
    input.files && input.files.length > 1
        ? `${input.files?.length} files`
        : input.files?.item(0)?.name ?? '';

export const getLargestFileSizeInBytes = (
    input: HTMLInputElement
): number | undefined =>
    input.files && input.files.length > 0
        ? Math.max(...Array.from(input.files).map((file) => file.size))
        : undefined;
